import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { toast } from "react-toastify";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Row,
  Spinner,
} from "reactstrap";
import CandidateCallBackModal from "pages/AllCommonModal/CandidateCallBackModal/candidate-call-back-modal";

import CommonTableForCandidate from "components/Common/CandidateTableContainer";
import LegacyFilterToolbar, {
  LEGACY_FILTER_INPUT_CLASS,
} from "components/Common/LegacyFilterToolbar";
import { fetchCandidateCallBackMetadata, saveCandidateCallBackRequest, submitCandidateCallBackFilterData } from "model/CandidateModel";
import Loaders from "components/Common/Loaders";
import { usePermissionContext } from "libs/context/PermissionContext";
import { useCommonFilterOptions } from "libs/context/CommonFilterOptionsContext";
import { DEFAULT_PAGE_SIZE, mapApiMetadataTableColumns, pascalToCamel } from "helpers/GeneralHelper";
import { useCandidateTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";

const formatDateForList = (value) => {
  if (!value) return "";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("MM/DD/YYYY hh:mm A") : value;
};

const mapRows = (rawRows = []) =>
  (Array.isArray(rawRows) ? rawRows : []).map((item) => ({
    ...item,
    id: item?.id || "",
    candidateClientId: item?.candidateClientId || "",
    /** Raw scheduled string from API — modal uses this for date/time fields. */
    scheduled: item?.scheduled || null,
    requestDate: item?.scheduled || null,
    requestDateText: formatDateForList(item?.scheduled),
    notes: item?.notes || "",
    callBackStatus: String(item?.callBackStatus || "active").toLowerCase(),
    status: String(item?.status || "active").toLowerCase(),
  }));

const strOrNull = (value) => {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim();
  return normalized === "" ? null : normalized;
};

const normalizeCallBackFilterPayload = (raw = {}) => ({
  nameContains: strOrNull(raw?.nameContains),
  fromDate: strOrNull(raw?.fromDate),
  toDate: strOrNull(raw?.toDate),
  pageNumber: Number(raw?.pageNumber) || 1,
  pageSize: Number(raw?.pageSize) || DEFAULT_PAGE_SIZE,
});

const resolveCallBackColumnAccessor = (col = {}) => {
  const accessor = pascalToCamel(col?.field);
  if (accessor === "requestDate") return "requestDateText";
  if (accessor === "callBackStatus") return "status";
  return null;
};

const CallBackRequest = () => {
  const { permissionData } = usePermissionContext() || {};
  const { fetchMetadataSchema } = useCommonFilterOptions();
  const { pageSize: settingsPageSize, settingsReady } =
    useCandidateTableSettingsPageSize("call-back-request");
  const initialFilterFetchedRef = useRef(false);
  const [metadataBootstrapDone, setMetadataBootstrapDone] = useState(false);
  const [candidateCallbackAddEditModalOpen, setCandidateCallbackAddEditModalOpen] =
    useState(false);
  const [filterForm, setFilterForm] = useState({
    search: "",
    fromDate: "",
    toDate: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tableMetaData, setTableMetaData] = useState([]);
  const [callbackRows, setCallbackRows] = useState([]);
  const [editableData, setEditableData] = useState(null);
  const [requestPayload, setRequestPayload] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    nameContains: null,
    fromDate: null,
    toDate: null,
  });
  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });

  const callBackRequestActionPermissions = useMemo(
    () => ({
      canCreate: Boolean(permissionData?.CandidateCallBackRequest),
      canEdit: Boolean(permissionData?.CandidateCallBackRequest),
    }),
    [permissionData]
  );

  const hasAnyCallBackRequestRowAction = useMemo(
    () => callBackRequestActionPermissions.canEdit,
    [callBackRequestActionPermissions]
  );

  const getCallBackList = async (payloadOverrides = {}) => {
    setLoading(true);
    try {
      const payload = normalizeCallBackFilterPayload({
        ...requestPayload,
        ...payloadOverrides,
      });
      const response = await submitCandidateCallBackFilterData(payload);
      const responseData = response?.data || {};
      const listData = responseData?.records ?? responseData?.data ?? responseData ?? [];
      const list = Array.isArray(listData) ? listData : [];

      if (response) {
        setCallbackRows(mapRows(list));
        setPaginationMeta({
          pageNumber: Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
          pageSize: Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
          totalPages: Number(responseData?.totalPages) || 0,
          totalRecords: Number(responseData?.totalRecords) || 0,
        });
        setRequestPayload(payload);
      } else {
        setCallbackRows([]);
      }
    } catch (error) {
      toast.error(error?.message || "Unable to load callback requests");
      setCallbackRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      setLoading(true);
      try {
        const metaRes = await fetchMetadataSchema("metadata:candidateCallback", () =>
          fetchCandidateCallBackMetadata({})
        );
        const meta = metaRes?.data ?? metaRes ?? {};
        const tableColumns = meta?.table?.columns?.length
          ? mapApiMetadataTableColumns(meta.table.columns, {
              resolveAccessor: resolveCallBackColumnAccessor,
            })
          : [];
        setTableMetaData(tableColumns);
      } catch (e) {
        toast.error(e?.message || "Unable to load callback request metadata");
        setTableMetaData([]);
      }

      setMetadataBootstrapDone(true);
    };
    bootstrap();
  }, []);

  /** 1) settings page size ready → 2) call filter API once */
  useEffect(() => {
    if (!settingsReady || !metadataBootstrapDone || initialFilterFetchedRef.current) return;
    initialFilterFetchedRef.current = true;
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    setRequestPayload((previous) => ({ ...previous, pageSize }));
    setPaginationMeta((previous) => ({ ...previous, pageSize }));
    void getCallBackList({ pageNumber: 1, pageSize });
  }, [settingsReady, settingsPageSize, metadataBootstrapDone]);

  const handleOpenCreate = () => {
    setEditableData(null);
    setCandidateCallbackAddEditModalOpen(true);
  };

  const handleEdit = (row) => {
    setEditableData(row || null);
    setCandidateCallbackAddEditModalOpen(true);
  };

  const handleFilterFieldChange = (e) => {
    const { name, value } = e.target;
    setFilterForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const buildPayloadFromFilterForm = (form = filterForm, overrides = {}) =>
    normalizeCallBackFilterPayload({
      pageNumber: 1,
      pageSize: requestPayload.pageSize || DEFAULT_PAGE_SIZE,
      nameContains: form?.search,
      fromDate: form?.fromDate,
      toDate: form?.toDate,
      ...overrides,
    });

  const handleSearchSubmit = async (value, extraFilters = {}) => {
    const trimmedValue = String(value || "").trim();
    await getCallBackList(
      buildPayloadFromFilterForm(
        {
          ...filterForm,
          ...extraFilters,
          search: trimmedValue,
        },
        { pageNumber: 1 }
      )
    );
  };

  const handlePageChange = async (page) => {
    await getCallBackList({ pageNumber: page });
  };

  const handleRowsPerPageChange = async (rows) => {
    await getCallBackList({ pageNumber: 1, pageSize: rows });
  };

  const handleSubmit = async (payload = {}) => {
    setSaving(true);
    try {
      const response = await saveCandidateCallBackRequest(payload);
      if (response?.success || response?.isSuccess || response?.status) {
        toast.success(response?.message || "Call back request saved successfully");
        setCandidateCallbackAddEditModalOpen(false);
        setEditableData(null);
        await getCallBackList();
      } else {
        toast.error(response?.message || "Unable to save callback request");
      }
    } finally {
      setSaving(false);
    }
  };

  const filteredData = useMemo(() => callbackRows, [callbackRows]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>

          <Card>
            <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="fw-bold mb-0">Call Back Request</h5>
                {/* <Button color="primary" onClick={handleOpenCreate}>
                  + Create
                </Button> */}
              </div>
              <hr />
              <LegacyFilterToolbar>
              <Row className="metadata-filter-toolbar__row g-2 g-md-3 align-items-end">
                <Col xs="12" md="4" className="metadata-filter-field metadata-filter-field--text">
                  <span className="manage-user-filter-label">Search</span>
                  <div className="metadata-filter-field__control">
                  <Input
                    type="text"
                    name="search"
                    placeholder="Search by name"
                    className={LEGACY_FILTER_INPUT_CLASS}
                    value={filterForm.search}
                    onChange={handleFilterFieldChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSearchSubmit(filterForm.search, filterForm);
                      }
                    }}
                  />
                  </div>
                </Col>
                <Col xs="12" md="3" className="metadata-filter-field metadata-filter-field--date">
                  <span className="manage-user-filter-label">From date</span>
                  <div className="metadata-filter-field__control">
                  <Input
                    type="date"
                    name="fromDate"
                    value={filterForm.fromDate}
                    onChange={handleFilterFieldChange}
                    className={LEGACY_FILTER_INPUT_CLASS}
                  />
                  </div>
                </Col>
                <Col xs="12" md="3" className="metadata-filter-field metadata-filter-field--date">
                  <span className="manage-user-filter-label">To date</span>
                  <div className="metadata-filter-field__control">
                  <Input
                    type="date"
                    name="toDate"
                    value={filterForm.toDate}
                    onChange={handleFilterFieldChange}
                    className={LEGACY_FILTER_INPUT_CLASS}
                  />
                  </div>
                </Col>
                <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--actions">
                  <span className="manage-user-filter-label" aria-hidden="true">&nbsp;</span>
                  <div className="metadata-filter-toolbar__actions">
                  <Button
                    color="primary"
                    className="w-100 manage-user-search-btn"
                    onClick={() => handleSearchSubmit(filterForm.search, filterForm)}
                  >
                    <i className="fa fa-search me-1" />
                    Search
                  </Button>
                  </div>
                </Col>
              </Row>
              </LegacyFilterToolbar>
              <div className="mb-3 h4 card-title"></div>
              {loading ? (
                <Loaders />

              ) : (
                <CommonTableForCandidate
                  columns={tableMetaData}
                  data={filteredData}
                  isActionRequired={hasAnyCallBackRequestRowAction}
                  onEdit={
                    callBackRequestActionPermissions.canEdit ? handleEdit : undefined
                  }
                  pageName="call-back-request"
                  isPagination={true}
                  currentPage={paginationMeta?.pageNumber}
                  rowsPerPage={paginationMeta?.pageSize}
                  totalPages={paginationMeta?.totalPages}
                  totalRecords={paginationMeta?.totalRecords}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handleRowsPerPageChange}
                />
              )}
            </CardBody>
          </Card>

          <CandidateCallBackModal
            setCandidateCallbackAddEditModalOpen={setCandidateCallbackAddEditModalOpen}
            candidateCallbackAddEditModalOpen={candidateCallbackAddEditModalOpen}
            editableData={editableData}
            onSubmit={handleSubmit}
            submitting={saving}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

CallBackRequest.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(CallBackRequest);
